import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Switch,
  Textarea,
} from '@nextui-org/react';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Dices,
  Plus,
  Search,
  Shield,
  Trophy,
  Users,
  XCircle,
} from 'lucide-react';
import adminPredictionsService, {
  AdminPredictionGame,
  AdminProTournamentOption,
} from '../../services/adminPredictionsService';

const ACCENT = '#3fced1';

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: 'all', label: 'Alle' },
  { key: 'draft', label: 'Entwürfe' },
  { key: 'open', label: 'Offen' },
  { key: 'running', label: 'Laufend' },
  { key: 'finished', label: 'Beendet' },
];

const statusChipClass = (status?: string | null): string => {
  switch (status) {
    case 'open': return 'bg-[#3fced1]/15 text-[#3fced1]';
    case 'running': return 'bg-emerald-500/15 text-emerald-400';
    case 'finished': return 'bg-zinc-700 text-gray-300';
    default: return 'bg-amber-500/15 text-amber-400'; // draft
  }
};

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">✕</button>
    </div>
  );
};

// =====================================================================
// Anlegen-Modal (Name, Turnier-Suche, Beschreibung, adminOnly)
// =====================================================================

const CreateGameModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onCreated: (game: AdminPredictionGame) => void;
  onError: (msg: string) => void;
}> = ({ isOpen, onClose, onCreated, onError }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [adminOnly, setAdminOnly] = useState(false);
  const [creating, setCreating] = useState(false);

  const [tournamentSearch, setTournamentSearch] = useState('');
  const [tournamentOptions, setTournamentOptions] = useState<AdminProTournamentOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<AdminProTournamentOption | null>(null);

  // Debounced Turnier-Suche
  useEffect(() => {
    if (!isOpen) return;
    const term = tournamentSearch.trim();
    setSearching(true);
    const timer = setTimeout(() => {
      adminPredictionsService
        .searchProTournaments(term || undefined)
        .then(setTournamentOptions)
        .catch(() => setTournamentOptions([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [tournamentSearch, isOpen]);

  const handleCreate = async () => {
    if (!name.trim() || !selectedTournament?._id) return;
    setCreating(true);
    try {
      const game = await adminPredictionsService.createGame({
        name: name.trim(),
        description: description.trim() || undefined,
        tournament: { ref: selectedTournament._id },
        adminOnly,
      });
      onCreated(game);
      setName('');
      setDescription('');
      setAdminOnly(false);
      setSelectedTournament(null);
      setTournamentSearch('');
      onClose();
    } catch (err) {
      onError(adminPredictionsService.getErrorMessage(err, 'Tippspiel konnte nicht angelegt werden.'));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} placement="center" scrollBehavior="inside" size="lg">
      <ModalContent className="border border-zinc-800 bg-zinc-900">
        <ModalHeader className="text-white">Neues Tippspiel</ModalHeader>
        <ModalBody>
          <Input
            autoFocus
            label="Name"
            placeholder='z.B. "World Matchplay Tippspiel 2026"'
            value={name}
            onValueChange={setName}
            classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
          />

          {/* Turnier-Auswahl mit Suche */}
          <div>
            <p className="mb-1.5 text-sm text-gray-400">Pro-Turnier (FlashLive) *</p>
            {selectedTournament ? (
              <div className="flex items-center justify-between gap-2 rounded-xl border border-[#3fced1]/40 bg-[#3fced1]/10 px-3 py-2.5">
                <span className="min-w-0 truncate text-sm font-semibold text-white">
                  {selectedTournament.name}
                  {selectedTournament.country?.name && (
                    <span className="ml-1.5 text-xs text-gray-400">({selectedTournament.country.name})</span>
                  )}
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedTournament(null)}
                  className="shrink-0 text-xs font-semibold text-gray-400 hover:text-white"
                >
                  Ändern
                </button>
              </div>
            ) : (
              <>
                <Input
                  placeholder="Turnier suchen …"
                  value={tournamentSearch}
                  onValueChange={setTournamentSearch}
                  startContent={<Search size={15} className="text-gray-500" />}
                  classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white' }}
                />
                <div className="mt-2 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-zinc-800 bg-zinc-950/60 p-1.5">
                  {searching ? (
                    <div className="flex justify-center py-4"><Spinner size="sm" color="primary" /></div>
                  ) : tournamentOptions.length === 0 ? (
                    <p className="px-2 py-3 text-center text-xs text-gray-500">Keine Turniere gefunden.</p>
                  ) : (
                    tournamentOptions.map((t) => (
                      <button
                        key={t._id}
                        type="button"
                        onClick={() => setSelectedTournament(t)}
                        className="flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-zinc-800"
                      >
                        <span className="min-w-0 truncate text-sm text-white">{t.name}</span>
                        {t.country?.name && (
                          <span className="shrink-0 text-[11px] text-gray-500">{t.country.name}</span>
                        )}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <Textarea
            label="Beschreibung (optional)"
            value={description}
            onValueChange={setDescription}
            minRows={2}
            classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
          />

          <div className="flex items-center justify-between gap-4 rounded-xl border border-zinc-800 bg-zinc-950/60 px-3 py-2.5">
            <div>
              <p className="flex items-center gap-1.5 text-sm font-semibold text-white">
                <Shield size={14} style={{ color: ACCENT }} /> Nur für Admins
              </p>
              <p className="mt-0.5 text-xs text-gray-400">
                Admin-Tippspiel: nur Admins sehen es (Listen, Startseite, App) und können sich anmelden.
              </p>
            </div>
            <Switch isSelected={adminOnly} onValueChange={setAdminOnly} aria-label="Nur für Admins" />
          </div>

          <p className="text-xs text-gray-500">
            Das Spiel startet als <strong>Entwurf</strong> – Termine, Punkte, Slides, Preise und
            Sponsoren pflegst du danach in der Detailansicht.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Abbrechen</Button>
          <Button
            isLoading={creating}
            isDisabled={!name.trim() || !selectedTournament}
            className="bg-[#3fced1] font-semibold text-black"
            onPress={handleCreate}
          >
            Anlegen
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// =====================================================================
// Seite
// =====================================================================

const AdminPredictionGamesPage: React.FC = () => {
  const navigate = useNavigate();
  const [games, setGames] = useState<AdminPredictionGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await adminPredictionsService.listGames();
      setGames(list);
    } catch (err) {
      setError(adminPredictionsService.getErrorMessage(err, 'Tippspiele konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = filter === 'all' ? games : games.filter((g) => g.status === filter);

  return (
    <>
      <Helmet>
        <title>Tippspiele | Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zum Admin-Dashboard"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-white">
              <Dices size={22} style={{ color: ACCENT }} />
              Tippspiele
            </h1>
            <p className="text-sm text-gray-400">
              Tippspiele anlegen, verwalten und auswerten.
            </p>
          </div>
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            startContent={<Plus size={16} />}
            onPress={() => setCreateOpen(true)}
          >
            Neues Tippspiel
          </Button>
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        {/* Status-Filter */}
        <div className="mb-5 flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                filter === f.key
                  ? 'border-transparent bg-[#3fced1] text-black'
                  : 'border-zinc-700 text-gray-300 hover:border-zinc-500'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((game) => (
              <Card
                key={game._id}
                isPressable
                onPress={() => navigate(`/admin/tippspiele/${game._id}`)}
                className="w-full border border-zinc-800 bg-zinc-900 transition-colors hover:border-[#3fced1]/50"
              >
                <CardBody className="flex flex-row items-center gap-4 p-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-bold text-white">{game.name}</h3>
                      <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${statusChipClass(game.status)}`}>
                        {adminPredictionsService.statusLabel(game.status)}
                      </span>
                      {game.adminOnly && (
                        <Chip size="sm" variant="flat" className="bg-purple-500/15 text-purple-400" startContent={<Shield size={11} />}>
                          Nur Admins
                        </Chip>
                      )}
                    </div>
                    <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1">
                        <Trophy size={12} /> {game.tournament?.name || 'Kein Turnier'}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Users size={12} /> {game.stats?.totalParticipants ?? 0} Teilnehmer
                      </span>
                      {game.slug && <span>/tippspiele/{game.slug}</span>}
                    </p>
                  </div>
                  <ChevronRight size={18} className="shrink-0 text-gray-500" />
                </CardBody>
              </Card>
            ))}

            {filtered.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <Dices size={40} className="text-zinc-700" />
                <p className="text-gray-500">
                  {filter === 'all' ? 'Noch keine Tippspiele. Lege das erste an!' : 'Keine Tippspiele mit diesem Status.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      <CreateGameModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(game) => {
          setFeedback(`"${game.name}" angelegt (Entwurf).`);
          setGames((prev) => [game, ...prev]);
          navigate(`/admin/tippspiele/${game._id}`);
        }}
        onError={setError}
      />
    </>
  );
};

export default AdminPredictionGamesPage;
